import { useEffect, useState } from "react";
import { Loader2, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["submitted", "reviewed", "arrived"];
const statusColor = { submitted: "bg-amber-100 text-amber-800", reviewed: "bg-blue-100 text-blue-800", arrived: "bg-green-100 text-green-800" };

export default function CheckinsPage() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("upcoming");
  const load = () => api.get("/checkins").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const update = async (id, body) => { await api.put(`/checkins/${id}`, body); toast.success("Updated"); load(); };
  const today = new Date().toISOString().slice(0, 10);
  const shown = (items || []).filter((c) => (filter === "upcoming" ? c.arrival_date >= today && c.status !== "arrived" : true));

  return (
    <div className="space-y-6" data-testid="checkins-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Online Check-in</h1>
          <p className="text-muted-foreground text-sm">Arrival details guests send before they arrive.</p>
        </div>
        <div className="flex gap-2">
          {["upcoming", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`checkins-filter-${f}`} className={`px-3 py-1.5 rounded-md text-sm capitalize ${filter === f ? "bg-primary text-primary-foreground" : "border border-border"}`}>{f}</button>
          ))}
        </div>
      </div>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : shown.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <ClipboardCheck className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No check-ins in this view.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((c) => (
            <div key={c.id} className="bg-card border border-border rounded-md p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`checkin-${c.id}`}>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="text-foreground font-medium">{c.name}</p>
                  <Badge className={`${statusColor[c.status]} border-0 capitalize`}>{c.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Arriving {c.arrival_date}{c.arrival_time ? ` at ${c.arrival_time}` : ""}{c.departure_date ? ` · leaving ${c.departure_date}` : ""} · {c.guests} guest{c.guests > 1 ? "s" : ""}</p>
                <p className="text-sm text-muted-foreground">{[c.email, c.phone, c.booking_ref && `Ref ${c.booking_ref}`].filter(Boolean).join(" · ")}</p>
                {c.requests && <p className="text-sm text-foreground italic">“{c.requests}”</p>}
              </div>
              <div className="flex items-center gap-2">
                <Input defaultValue={c.room} placeholder="Room" className="w-24" onBlur={(e) => e.target.value !== (c.room || "") && update(c.id, { room: e.target.value })} data-testid={`checkin-room-${c.id}`} />
                <Select value={c.status} onValueChange={(v) => update(c.id, { status: v })}>
                  <SelectTrigger className="w-36" data-testid={`checkin-status-${c.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
